import Circle from '@arcgis/core/geometry/Circle';
import Graphic from '@arcgis/core/Graphic';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import { DEFAULT_MAP_SOURCE } from '@env';
import { Point } from 'geojson';

import { EsriMap } from '@lib/geo/esri';
import { FsMap } from '@lib/geo/map';

export class FsRadiusCircle {
  public circleData: any;
  public center: Point;

  private _circle: google.maps.Circle | Graphic;

  get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  constructor(
    private readonly _map: FsMap,
    private geometry: Point,
    private radius: number,
    options?: any,
  ) {
    this.circleData = options;

    if (this.defaultMapSource === 'google' && this._map) {
      this._initGoogleRadiusCircle();
    } else {
      this._initEsriRadiusCircle();
    }
  }

  get centerPoint(): Point {
    return this.geometry;
  }

  public clearCircles(): void {
    if (this._circle) {
      if (this.defaultMapSource === 'google') {
        (this._circle as unknown as google.maps.Circle).setMap(null);
      } else {
        (this._map as unknown as EsriMap).mapView.graphics.remove(
          this._circle as unknown as Graphic,
        );
      }
    }
  }

  public radiusFilter(radius: number): void {
    if (this.defaultMapSource === 'google') {
      const lonLatBounds = (this._circle as unknown as google.maps.Circle).getBounds();
      (this._map as unknown as google.maps.Map).fitBounds(lonLatBounds);
    } else {
      if (this._circle) {
        const circleExtent = (this._circle as unknown as Graphic).geometry.extent.expand(1.5); // Expand to provide padding
        (this._map as unknown as EsriMap).mapView.goTo(circleExtent); // Fit the view to the circle's extent
      }
    }
  }

  private _initGoogleRadiusCircle() {
    const geometry = new google.maps.LatLng(
      this.geometry.coordinates[1],
      this.geometry.coordinates[0],
    );
    this._circle = new google.maps.Circle({
      strokeColor: '#FF0000',
      strokeOpacity: 0.4,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.08,
      map: this._map.mapInstance as unknown as google.maps.Map,
      center: geometry,
      radius: this.radius * 1609.344,
    });
    this._circle.setMap(this._map.mapInstance as unknown as google.maps.Map);
  }

  private _initEsriRadiusCircle() {
    const point = {
      type: 'point',
      longitude: this.geometry.coordinates[0],
      latitude: this.geometry.coordinates[1],
    };

    const circleGeometry = new Circle({
      center: point,
      radius: this.radius * 1609.344, // Convert miles to meters
      geodesic: true,
    });

    const circleSymbol = new SimpleFillSymbol({
      color: [255, 0, 0, 0.08], // Red with 8% opacity
      outline: {
        color: [255, 0, 0, 0.4],
        width: 2,
      },
    });

    this._circle = new Graphic({
      geometry: circleGeometry,
      symbol: circleSymbol,
    });

    (this._map as unknown as EsriMap).mapView.graphics.add(this._circle);
  }
}
